/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, NavigateFunction } from 'react-router-dom'

import {
  ReportNode,
  ComplaintsReportSourceChoices,
  useReportQuery,
} from '@/apollo/graphql'
import { homePath, transformData } from '@/utils'
import { IReport, TFormSubmit } from '@/types'
import { useAPI } from '@/hooks'

interface IUseData {
  id?: string
}

interface IUseDataRes {
  loading: boolean
  submitting: boolean
  data: Partial<ReportNode> | undefined
  onSubmit: TFormSubmit<ReportNode>
  onCancel: () => void
}

const defaultData: Partial<IReport> = {
  source: ComplaintsReportSourceChoices.Police,
}

export const useData = ({ id }: IUseData): IUseDataRes => {
  const navigate: NavigateFunction = useNavigate()
  const { submitting, sendForm } = useAPI()
  const [data, setData] = useState<Partial<IReport>>(defaultData)

  const { loading, refetch } = useReportQuery({
    skip: !id,
    fetchPolicy: 'network-only',
    variables: {
      id: id || '',
    },
    onCompleted: res => {
      if (res.report) {
        // @ts-ignore
        setData({
          ...(res.report || {}),
          source: res.report.source || ComplaintsReportSourceChoices.Police,
        })
      }
    },
  })

  useEffect(() => {
    if (!id) {
      setData(defaultData)
    }
  }, [id])

  const onSubmit: TFormSubmit<IReport> = useCallback(
    (path, isEdit, callback) => d => {
      if (d.crimeTime) {
        d.crimeTime = new Date(d.crimeTime).toISOString()
      }
      if (d.issueTime) {
        d.issueTime = new Date(d.issueTime).toISOString()
      }
      sendForm(
        {
          path,
          method: isEdit ? 'PUT' : 'POST',
          data: transformData(d),
          callback: e => {
            callback?.(e)
            refetch()
          },
        },
        { showNotify: true },
      )
    },
    [refetch, sendForm],
  )

  const onCancel = useCallback(() => {
    navigate(homePath)
  }, [navigate])

  return {
    loading,
    submitting,
    data,
    onSubmit,
    onCancel,
  }
}
